<script lang="ts" setup>
const props = defineProps({
  content: {
    default: '',
    type: String,
  },
  maxCharacters: {
    default: 0,
    type: Number,
  },
})

const { t } = useI18n()

const showAll = ref(false)

const finalContent = computed(() => {
  if (props.maxCharacters && typeof props.content === 'string') {
    return ellipsize(
      props.content,
      showAll.value ? Number.POSITIVE_INFINITY : props.maxCharacters,
    )
  }

  return props.content as string
})

function ellipsize(inputString: string, maxLength: number) {
  if (inputString.length <= maxLength) {
    return inputString
  }
  else {
    // If the input string is longer than the maximum length, find the last full word that fits
    const truncatedString = inputString.slice(0, maxLength)
    const lastSpaceIndex = truncatedString.lastIndexOf(' ')

    // If there's no space in the truncated string, we can't avoid cutting a word
    if (lastSpaceIndex === -1) {
      return `${truncatedString.slice(0, maxLength - 3)}...`
    }

    // Return the string truncated at the last full word, with an ellipsis appended
    return `${truncatedString.slice(0, lastSpaceIndex)}...`
  }
}

function onShowMore() {
  showAll.value = true
}
</script>

<template>
  <p>
    <span v-if="maxCharacters || typeof content === 'string'">
      {{ finalContent }}
    </span>

    <span
      v-if="maxCharacters && typeof content === 'string' && finalContent?.length !== content.length"
      class="ml-1 cursor-pointer text-gray-600 hover:underline dark:text-gray-400"
      @click="onShowMore"
    >
      {{ t('showMore') }}
    </span>
  </p>
</template>

<i18n lang="json">
{
  "en": {
    "showMore": "(show more)"
  },
  "es": {
    "showMore": "(mostrar más)"
  },
  "fr": {
    "showMore": "(afficher)"
  }
}
</i18n>
